import { storage } from './adapter';
import { STORAGE_KEYS } from '@/shared/constants';
class DefaultDataAccessLayer {
    // 剧本相关操作
    async getScript(id) {
        const scripts = await this.getAllScripts();
        return scripts.find(script => script.id === id) || null;
    }
    async getAllScripts() {
        try {
            const scripts = await storage.getItem(STORAGE_KEYS.GAME_SCRIPTS);
            return scripts || [];
        }
        catch (error) {
            console.error('Error loading scripts:', error);
            return [];
        }
    }
    async saveScript(script) {
        const scripts = await this.getAllScripts();
        const existingIndex = scripts.findIndex(s => s.id === script.id);
        if (existingIndex >= 0) {
            scripts[existingIndex] = script;
        }
        else {
            scripts.push(script);
        }
        await storage.setItem(STORAGE_KEYS.GAME_SCRIPTS, scripts);
    }
    async updateScript(script) {
        const updatedScript = {
            ...script,
            updatedAt: Date.now(),
        };
        await this.saveScript(updatedScript);
    }
    async deleteScript(id) {
        const scripts = await this.getAllScripts();
        const filteredScripts = scripts.filter(script => script.id !== id);
        await storage.setItem(STORAGE_KEYS.GAME_SCRIPTS, filteredScripts);
        // 同时删除相关的会话
        const sessions = await this.getAllSessions();
        const filteredSessions = sessions.filter(session => session.scriptId !== id);
        await storage.setItem(STORAGE_KEYS.GAME_SESSIONS, filteredSessions);
    }
    async getScriptMetas() {
        const scripts = await this.getAllScripts();
        return scripts.map(script => ({
            id: script.id,
            name: script.name,
            description: script.description,
            category: script.category,
            tags: script.tags,
            author: script.author,
            createdAt: script.createdAt,
            rating: script.rating,
            playCount: script.playCount,
        }));
    }
    // 会话相关操作
    async getSession(id) {
        const sessions = await this.getAllSessions();
        return sessions.find(session => session.id === id) || null;
    }
    async getAllSessions() {
        try {
            const sessions = await storage.getItem(STORAGE_KEYS.GAME_SESSIONS);
            return sessions || [];
        }
        catch (error) {
            console.error('Error loading sessions:', error);
            return [];
        }
    }
    async saveSession(session) {
        const sessions = await this.getAllSessions();
        const existingIndex = sessions.findIndex(s => s.id === session.id);
        if (existingIndex >= 0) {
            sessions[existingIndex] = session;
        }
        else {
            sessions.push(session);
        }
        await storage.setItem(STORAGE_KEYS.GAME_SESSIONS, sessions);
    }
    async updateSession(session) {
        const updatedSession = {
            ...session,
            updatedAt: Date.now(),
        };
        await this.saveSession(updatedSession);
    }
    async deleteSession(id) {
        const sessions = await this.getAllSessions();
        const filteredSessions = sessions.filter(session => session.id !== id);
        await storage.setItem(STORAGE_KEYS.GAME_SESSIONS, filteredSessions);
        // 删除相关的存档点
        const savePoints = await storage.getItem(STORAGE_KEYS.SAVE_POINTS) || [];
        const filteredSavePoints = savePoints.filter(savePoint => savePoint.sessionId !== id);
        await storage.setItem(STORAGE_KEYS.SAVE_POINTS, filteredSavePoints);
    }
    async getSessionMetas() {
        const sessions = await this.getAllSessions();
        return sessions.map(session => ({
            id: session.id,
            name: session.name,
            scriptName: session.scriptName,
            description: session.description,
            createdAt: session.createdAt,
            updatedAt: session.updatedAt,
            lastPlayedAt: session.lastPlayedAt,
            isActive: session.isActive,
            isCompleted: session.isCompleted,
        }));
    }
    // 存档相关操作
    async getSavePoint(id) {
        const savePoints = await storage.getItem(STORAGE_KEYS.SAVE_POINTS) || [];
        return savePoints.find(savePoint => savePoint.id === id) || null;
    }
    async getSavePointsForSession(sessionId) {
        const savePoints = await storage.getItem(STORAGE_KEYS.SAVE_POINTS) || [];
        return savePoints.filter(savePoint => savePoint.sessionId === sessionId);
    }
    async saveSavePoint(savePoint) {
        const savePoints = await storage.getItem(STORAGE_KEYS.SAVE_POINTS) || [];
        const existingIndex = savePoints.findIndex(sp => sp.id === savePoint.id);
        if (existingIndex >= 0) {
            savePoints[existingIndex] = savePoint;
        }
        else {
            savePoints.push(savePoint);
        }
        await storage.setItem(STORAGE_KEYS.SAVE_POINTS, savePoints);
    }
    async deleteSavePoint(id) {
        const savePoints = await storage.getItem(STORAGE_KEYS.SAVE_POINTS) || [];
        const filteredSavePoints = savePoints.filter(savePoint => savePoint.id !== id);
        await storage.setItem(STORAGE_KEYS.SAVE_POINTS, filteredSavePoints);
    }
    // 通用操作
    async exportData() {
        const [scripts, sessions, savePoints] = await Promise.all([
            this.getAllScripts(),
            this.getAllSessions(),
            storage.getItem(STORAGE_KEYS.SAVE_POINTS) || []
        ]);
        const exportData = {
            version: '0.1.0',
            exportedAt: new Date().toISOString(),
            data: {
                scripts,
                sessions,
                savePoints,
            },
        };
        return JSON.stringify(exportData, null, 2);
    }
    async importData(jsonData) {
        try {
            const importData = JSON.parse(jsonData);
            if (!importData.data) {
                throw new Error('Invalid import data format');
            }
            const { scripts = [], sessions = [], savePoints = [] } = importData.data;
            // 合并数据而不是替换
            const existingScripts = await this.getAllScripts();
            const existingSessions = await this.getAllSessions();
            const existingSavePoints = await storage.getItem(STORAGE_KEYS.SAVE_POINTS) || [];
            // 合并剧本（避免重复）
            const mergedScripts = [...existingScripts];
            for (const script of scripts) {
                if (!mergedScripts.find(s => s.id === script.id)) {
                    mergedScripts.push(script);
                }
            }
            // 合并会话（避免重复）
            const mergedSessions = [...existingSessions];
            for (const session of sessions) {
                if (!mergedSessions.find(s => s.id === session.id)) {
                    mergedSessions.push(session);
                }
            }
            // 合并存档点（避免重复）
            const mergedSavePoints = [...existingSavePoints];
            for (const savePoint of savePoints) {
                if (!mergedSavePoints.find(sp => sp.id === savePoint.id)) {
                    mergedSavePoints.push(savePoint);
                }
            }
            // 保存合并后的数据
            await Promise.all([
                storage.setItem(STORAGE_KEYS.GAME_SCRIPTS, mergedScripts),
                storage.setItem(STORAGE_KEYS.GAME_SESSIONS, mergedSessions),
                storage.setItem(STORAGE_KEYS.SAVE_POINTS, mergedSavePoints),
            ]);
        }
        catch (error) {
            throw new Error(`Failed to import data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    async clearAllData() {
        await Promise.all([
            storage.removeItem(STORAGE_KEYS.GAME_SCRIPTS),
            storage.removeItem(STORAGE_KEYS.GAME_SESSIONS),
            storage.removeItem(STORAGE_KEYS.SAVE_POINTS),
            storage.removeItem(STORAGE_KEYS.RECENT_SCRIPTS),
            storage.removeItem(STORAGE_KEYS.RECENT_SESSIONS),
        ]);
    }
}
// 数据验证工具
export class DataValidator {
    static validateScript(script) {
        return (typeof script === 'object' &&
            script !== null &&
            typeof script.id === 'string' &&
            typeof script.name === 'string' &&
            typeof script.description === 'string' &&
            typeof script.systemPrompt === 'string' &&
            Array.isArray(script.characterTemplates) &&
            typeof script.createdAt === 'number');
    }
    static validateSession(session) {
        return (typeof session === 'object' &&
            session !== null &&
            typeof session.id === 'string' &&
            typeof session.scriptId === 'string' &&
            typeof session.name === 'string' &&
            Array.isArray(session.messages) &&
            typeof session.gameState === 'object' &&
            typeof session.currentCharacter === 'object' &&
            typeof session.createdAt === 'number');
    }
    static validateSavePoint(savePoint) {
        return (typeof savePoint === 'object' &&
            savePoint !== null &&
            typeof savePoint.id === 'string' &&
            typeof savePoint.sessionId === 'string' &&
            typeof savePoint.name === 'string' &&
            typeof savePoint.messageIndex === 'number' &&
            typeof savePoint.timestamp === 'number');
    }
}
// 数据迁移工具
export class DataMigrator {
    static async migrate() {
        // 这里可以实现数据版本迁移逻辑
        // 例如从旧版本的数据格式迁移到新版本
        console.log('Data migration completed');
    }
    static async checkDataIntegrity() {
        const errors = [];
        try {
            // 验证剧本数据
            const scripts = await dal.getAllScripts();
            for (const script of scripts) {
                if (!DataValidator.validateScript(script)) {
                    errors.push(`Invalid script data: ${script.id || 'unknown'}`);
                }
            }
            // 验证会话数据
            const sessions = await dal.getAllSessions();
            for (const session of sessions) {
                if (!DataValidator.validateSession(session)) {
                    errors.push(`Invalid session data: ${session.id || 'unknown'}`);
                }
            }
            // 验证存档点数据
            const savePoints = await storage.getItem(STORAGE_KEYS.SAVE_POINTS) || [];
            for (const savePoint of savePoints) {
                if (!DataValidator.validateSavePoint(savePoint)) {
                    errors.push(`Invalid save point data: ${savePoint.id || 'unknown'}`);
                }
            }
        }
        catch (error) {
            errors.push(`Data integrity check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        return {
            valid: errors.length === 0,
            errors,
        };
    }
}
// 默认数据访问层实例
export const dal = new DefaultDataAccessLayer();
// 初始化数据存储
export async function initializeStorage() {
    try {
        // 运行数据迁移
        await DataMigrator.migrate();
        // 检查数据完整性
        const integrity = await DataMigrator.checkDataIntegrity();
        if (!integrity.valid) {
            console.warn('Data integrity issues found:', integrity.errors);
        }
        console.log('Storage initialized successfully');
    }
    catch (error) {
        console.error('Failed to initialize storage:', error);
        throw error;
    }
}
